import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getValueInUsd } from "@src/appV2/lib/Money/utils/currency";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useQueryClient } from "@tanstack/react-query";
import { max, parseISO } from "date-fns";
import { useEffect } from "react";

import { getMinutesElapsedWithTargetArrival } from "../../ArrivalTime/calculateMinutesElapsed";
import { getUrgentArrivalPillLabel } from "../../ArrivalTime/getUrgentArrivalPillLabel";
import {
  SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getScheduledShiftDurationInMinutes } from "../../utils/getScheduledShiftDuration";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { invalidateShiftModal } from "../Open/useGetShiftModal";
import { type ModalShiftData } from "../types";
import { useCreateShiftAuctionBid } from "./api/useCreateShiftAuctionBid";
import { SHIFT_AUCTION_LABEL } from "./constants";
import { ReviewBidBottomSheet } from "./ReviewBidBottomSheet";

export interface ReviewBidBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  inputBidPay: number;
}

export function ReviewBidBottomSheetContainer(props: ReviewBidBottomSheetContainerProps) {
  const { modalState, shift, inputBidPay } = props;

  const {
    id: shiftId,
    workplace,
    shiftAuction,
    shiftAuctionBid,
    attributes: { start, end, isInstantPay },
  } = shift;

  const {
    requiresLunchBreak,
    name: workplaceName,
    location: { timezone },
  } = workplace.attributes;

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const { navigateToModal, computedArrivalData } = useShiftModalsDataContext();
  const { showInfoToast, showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace.id,
  });

  const { mutate: createBid, isLoading: isCreatingBid } = useCreateShiftAuctionBid({
    onSuccess: async () => {
      showSuccessToast("Bid placed!");
      await invalidateShiftModal(queryClient, shiftId);
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    },
    onError: () => {
      showErrorToast("Error placing bid");
    },
  });

  const handleBack = () => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH, {
      pathParams: { shiftId },
    });
  };

  // This may happen if the shift is no longer available for bidding
  useEffect(() => {
    if (!shiftAuction) {
      showInfoToast(`No ${SHIFT_AUCTION_LABEL} auction, returning to shift details`);
      logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_REVIEW_BID_NO_AUCTION, { shiftId });
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    }
  }, [shiftAuction, shiftId, navigateToModal, showInfoToast]);

  const resolvedTimezone = timezone ?? "America/Los_Angeles";
  const urgentPillLabel = getUrgentArrivalPillLabel(
    computedArrivalData?.targetArrivalTime,
    resolvedTimezone
  );

  if (!shiftAuction) {
    return null;
  }

  const scheduledShiftDurationInHours = getScheduledShiftDurationInMinutes({ start, end }) / 60;
  const minutesElapsed = getMinutesElapsedWithTargetArrival({
    shiftStartDate: parseISO(start),
    baseReferenceDate: max([shiftAuction.attributes.endTime, new Date()]),
    targetArrivalTime: computedArrivalData?.targetArrivalTime,
  });

  const remainingWorkDurationInHours = getShiftWorkDurationInHours(
    { start, end },
    Boolean(requiresLunchBreak),
    mandatoryBreakDurationInMinutes,
    minutesElapsed
  );

  const auctionBidAmount = shiftAuctionBid
    ? getValueInUsd(shiftAuctionBid.attributes.bidAmount.amountInMinorUnits)
    : undefined;

  const handleConfirmBid = () => {
    createBid({
      shiftAuctionId: shiftAuction.id,
      bidAmount: inputBidPay,
      computedArrivalData,
    });
  };

  if (!shiftAuction) {
    return null;
  }

  return (
    <ReviewBidBottomSheet
      modalState={modalState}
      workplaceName={workplaceName}
      shiftStart={startDate}
      shiftEnd={endDate}
      scheduledShiftDurationInHours={scheduledShiftDurationInHours}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={Boolean(requiresLunchBreak)}
      remainingWorkDurationInHours={remainingWorkDurationInHours}
      minutesElapsed={minutesElapsed}
      timezone={resolvedTimezone}
      auctionEndTime={shiftAuction.attributes.endTime}
      inputBidPay={inputBidPay}
      auctionBidAmount={auctionBidAmount}
      isInstantPay={Boolean(isInstantPay)}
      isLoading={isCreatingBid}
      urgentPillLabel={urgentPillLabel}
      onConfirmBid={handleConfirmBid}
      onBack={handleBack}
    />
  );
}
